import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AdminSubModule, AdminSubModuleAbilities, PrismModule, SubmoduleCard } from '@prism/prism-shared';
import { BehaviorSubject, combineLatest, filter, from, map, Observable } from 'rxjs';
import { AppwriteAuthService } from '../shared/services/appwrite-auth.service';
import { CoreSelectors } from '../shared/state/core';

@Component({
  selector: 'prism-client-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminComponent {


  public baseAdminRouteActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public cards$!: Observable<SubmoduleCard[]>;


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private auth: AppwriteAuthService,
  ) { }

  ngOnInit() {
    if (this.route.snapshot.url.length > 0) {
      this.baseAdminRouteActive$.next(false);
    }

    const currentUser$ = this.store.select(CoreSelectors.getCurrentUser).pipe(
      filter(user => !!user),
    );

    this.cards$ = combineLatest([currentUser$, from(this.auth.currentUser)]).pipe(
      map(([user, account]) => {
        if (!user.abilities[PrismModule.Admin].visible) {
          return [];
        }
        const cards: SubmoduleCard[] = [];
        if (account?.labels.includes('prismuseradmin')) {
          cards.push({ name: 'Prism MCP', description: 'Manage bot tokens and read permissions',
            icon: '/assets/icons/gear.svg', path: `${PrismModule.Admin}/prism-mcp` });
        }
        const adminAbilities: AdminSubModuleAbilities = user.abilities[PrismModule.Admin]
        if (adminAbilities[AdminSubModule.ManageUsers].visible) {
          cards.push({
            name: 'user management',
            description: 'Create and update users and permissions',
            icon: '/assets/icons/user.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.ManageUsers}`,
          })
        }
        if (adminAbilities[AdminSubModule.OnsiteUtilities].visible) {
          cards.push({
            name: 'onsite utilities',
            description: 'quick links to onsite service sites',
            icon: '/assets/icons/gear.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.OnsiteUtilities}`,
          })
        }
        if (adminAbilities[AdminSubModule.Utilities].visible) {
          cards.push({
            name: 'utilities',
            description: 'various buttons that do things',
            icon: '/assets/icons/gear.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.Utilities}`,
          })
        }
        if (adminAbilities[AdminSubModule.LabelPrinterGroups]?.visible) {
          cards.push({
            name: 'label printer groups',
            description: 'group label printers and choose which pages can use them',
            icon: '/assets/icons/printer.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.LabelPrinterGroups}`,
          })
        }
        if (adminAbilities[AdminSubModule.CronJobs].visible) {
          cards.push({
            name: 'cron jobs',
            description: 'view and test all of our cron jobs',
            icon: '/assets/icons/clock.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.CronJobs}`
          })
        }
        if (adminAbilities[AdminSubModule.Collections].visible) {
          cards.push({
            name: 'collections',
            description: 'copy and delete collections',
            icon: '/assets/icons/user.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.Collections}`
          })
        }
        if (adminAbilities[AdminSubModule.Migrations].visible) {
          cards.push({
            name: 'migrations',
            description: 'handles all data migrations',
            icon: '/assets/icons/user.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.Migrations}`,
          })
        }
        if (adminAbilities[AdminSubModule.SvgPreview]?.visible) {
          cards.push({
            name: 'svg preview',
            description: 'preview svg files and copy path',
            icon: '/assets/icons/image.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.SvgPreview}`
          })
        }
        if (adminAbilities[AdminSubModule.Cribbage].visible) {
          cards.push({
            name: 'cribbage',
            description: 'keep track of the game',
            icon: '/assets/icons/shapes.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.Cribbage}`
          })
        }
        if (adminAbilities[AdminSubModule.DataserverManagement].visible) {
          cards.push({
            name: 'dataserver management',
            description: 'utilities to test and manage the dataservers',
            icon: '/assets/icons/data_alert.svg',
            path: `${PrismModule.Admin}/${AdminSubModule.DataserverManagement}`
          })
        }
        // Dev preview of the printable plastics invoice (hard-coded data). Not
        // ability-gated: anyone who can reach admin sees it.
        cards.push({
          name: 'invoice preview',
          description: 'preview the printable plastics invoice with sample data',
          icon: '/assets/icons/gear.svg',
          path: `${PrismModule.Admin}/invoice-preview`,
        })
        return cards;
      })
    )
  }

  public navigateToModule(url: string) {
    this.router.navigate([url]);
  }
}
